import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["materialsSummary", "variantSelect", "quantityInput", "bulkQuantity"]
  static values = {
    objectType: String
  }

  connect() {
    this.updateMaterials()

    // Use MutationObserver to watch for DOM changes (Cocoon events don't work)
    this.observer = new MutationObserver((mutations) => {
      // Check if any nested-fields were added or removed, or if _destroy inputs changed
      const shouldUpdate = mutations.some(mutation => {
        // Check for added/removed nodes
        if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
          return true
        }
        // Check for changes to _destroy inputs
        if (mutation.type === 'attributes' && mutation.target.name && mutation.target.name.includes('_destroy')) {
          return true
        }
        return false
      })

      if (shouldUpdate) {
        this.updateMaterials()
      }
    })

    // Observe the batch-items container for changes
    const batchItemsContainer = this.element.querySelector('#batch-items')
    if (batchItemsContainer) {
      this.observer.observe(batchItemsContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value']
      })
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  updateMaterials() {
    // Add a small delay to ensure DOM is updated (especially for remove actions)
    setTimeout(() => {
      this.performUpdate()
    }, 100)
  }

  performUpdate() {
    const items = this.collectItems()

    if (items.length === 0) {
      this.showEmptyState()
      return
    }

    const params = new URLSearchParams()

    params.append(`object_type`, this.objectTypeValue)

    // Get use_blend checkbox value
    const useBlendCheckbox = this.element.querySelector('input[type="checkbox"][name*="[use_blend]"]')
    if (useBlendCheckbox) {
      params.append(`use_blend`, useBlendCheckbox.checked)
    } else {
      params.append(`use_blend`, false)
    }

    items.forEach((item, index) => {
      params.append(`items[${index}][variant_id]`, item.variantId)
      params.append(`items[${index}][quantity]`, item.quantity)
    })

    const url = `/crafting/object_batches/calculate`

    fetch(`${url}?${params.toString()}`, {
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.text())
    .then(html => {
      if (this.hasMaterialsSummaryTarget) {
        this.materialsSummaryTarget.innerHTML = html
      }
    })
    .catch(error => {
      console.error('Error calculating materials:', error)
      this.showError()
    })
  }

  collectItems() {
    const items = []

    // Find all nested-fields that are not marked for destruction
    const nestedFields = this.element.querySelectorAll('.nested-fields')

    nestedFields.forEach(field => {
      // Skip if marked for destruction
      const destroyInput = field.querySelector('input[name*="_destroy"]')
      if (destroyInput && destroyInput.value === '1') {
        return
      }

      // Look for variant_id in the field - could be different field names based on object type
      const variantSelect = this.findVariantSelect(field)
      const quantityInput = field.querySelector('input[name*="[quantity]"]')

      if (variantSelect && quantityInput) {
        const variantId = variantSelect.value
        const quantity = parseInt(quantityInput.value) || 0

        if (variantId && quantity > 0) {
          items.push({ variantId, quantity })
        }
      }
    })

    return items
  }

  findVariantSelect(field) {
    // Try different variant field names based on object type
    const possibleSelectors = [
      'select[name*="[variant_id]"]',
      'select[name*="[perfumed_candle_variant_id]"]',
      'select[name*="[wax_melt_variant_id]"]',
      'select[name*="[jesmonite_object_variant_id]"]'
    ]

    for (const selector of possibleSelectors) {
      const select = field.querySelector(selector)
      if (select) return select
    }

    return null
  }

  setAllQuantities() {
    const value = parseInt(this.bulkQuantityTarget.value)
    if (!value || value < 1) return

    this.element.querySelectorAll('.nested-fields').forEach(field => {
      const destroyInput = field.querySelector('input[name*="_destroy"]')
      if (destroyInput && destroyInput.value === '1') return

      const quantityInput = field.querySelector('input[name*="[quantity]"]')
      if (quantityInput) quantityInput.value = value
    })

    this.updateMaterials()
  }

  showEmptyState() {
    if (!this.hasMaterialsSummaryTarget) return

    const objectName = this.hasObjectTypeValue ? this.objectTypeValue : 'object'
    this.materialsSummaryTarget.innerHTML = `
      <div class="alert alert-info">
        Select variants and quantities above to see the materials required.
      </div>
    `
  }

  showError() {
    if (!this.hasMaterialsSummaryTarget) return

    this.materialsSummaryTarget.innerHTML = `
      <div class="alert alert-danger">
        Error calculating materials. Please try again.
      </div>
    `
  }
}

